import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient, useSession } from "../lib/auth.ts";
import { api } from "../lib/api.ts";
import { EmptyState, InboxIcon } from "../components/EmptyState.tsx";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";

type Invitation = {
  id: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string | Date;
  inviterId: string;
};

const ROLES = ["member", "admin"] as const;

export function OrganisationPage() {
  const qc = useQueryClient();
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const myUserId = session?.user?.id;

  // Org name/slug (still via Better-Auth — keeps the membership-aware
  // permissions checks we don't want to replicate). Members come from our
  // own endpoint so we get the custom blockedAt/blockedReason fields too.
  const fullOrg = useQuery({
    queryKey: ["org", orgId, "full"],
    queryFn: async () => {
      const r = await authClient.organization.getFullOrganization();
      return r.data;
    },
    enabled: !!orgId,
  });

  const myMember = (fullOrg.data?.members ?? []).find((m) => m.userId === myUserId);
  const myRole = (myMember?.role ?? "member") as string;
  const canManage = myRole === "owner" || myRole === "admin";
  const isOwner = myRole === "owner";

  const enriched = useQuery({
    queryKey: ["org", orgId, "members"],
    queryFn: () => api.listMembers(orgId!),
    enabled: !!orgId,
  });
  const members = enriched.data?.members ?? [];
  const memberSort = useSortableTable(members, {
    name:      (m) => m.name,
    email:     (m) => m.email,
    role:      (m) => m.role,
    blocked:   (m) => (m.blockedAt ? 1 : 0),
    createdAt: (m) => m.createdAt,
  }, "createdAt", "desc");

  const invitations = useQuery({
    queryKey: ["org", orgId, "invitations"],
    queryFn: async () => {
      const r = await authClient.organization.listInvitations();
      return (r.data ?? []) as unknown as Invitation[];
    },
    enabled: !!orgId && canManage,
  });

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<typeof ROLES[number]>("member");
  const [inviteState, setInviteState] = useState<{ busy: boolean; err?: string; ok?: string }>({ busy: false });

  const refetchAll = async () => {
    await qc.invalidateQueries({ queryKey: ["org", orgId, "full"] });
    await qc.invalidateQueries({ queryKey: ["org", orgId, "members"] });
    await qc.invalidateQueries({ queryKey: ["org", orgId, "invitations"] });
  };

  // Rename — owner only (Better-Auth gates this server-side anyway).
  const [renameDraft, setRenameDraft] = useState<string>("");
  const [renameMsg, setRenameMsg] = useState<{ ok?: string; err?: string } | null>(null);
  useEffect(() => {
    if (fullOrg.data?.name && !renameDraft) setRenameDraft(fullOrg.data.name);
  }, [fullOrg.data?.name, renameDraft]);

  const blockMember = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      api.blockMember(orgId!, id, reason),
    onSuccess: refetchAll,
  });
  const unblockMember = useMutation({
    mutationFn: (id: string) => api.unblockMember(orgId!, id),
    onSuccess: refetchAll,
  });
  const memberMutErr = (blockMember.error || unblockMember.error) as Error | undefined;

  return (
    <>
      <h1>Organisation</h1>

      <div className="card">
        {isOwner ? (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setRenameMsg(null);
              const trimmed = renameDraft.trim();
              if (!trimmed || trimmed === fullOrg.data?.name) return;
              const r = await authClient.organization.update({
                data: { name: trimmed },
                organizationId: orgId!,
              });
              if (r.error) {
                setRenameMsg({ err: r.error.message ?? "Couldn't rename." });
                return;
              }
              setRenameMsg({ ok: "Renamed." });
              await refetchAll();
            }}
            style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end", flexWrap: "wrap" }}
          >
            <label style={{ flex: "1 1 240px" }}>
              <span className="muted">Organisation name</span>
              <input
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                aria-label="Organisation name"
              />
            </label>
            <button
              type="submit"
              disabled={!renameDraft.trim() || renameDraft.trim() === fullOrg.data?.name}
            >
              Save
            </button>
            {renameMsg?.err && <span className="err">{renameMsg.err}</span>}
            {renameMsg?.ok && <span className="muted">{renameMsg.ok}</span>}
          </form>
        ) : (
          <h3>{fullOrg.data?.name ?? "—"}</h3>
        )}
        <div className="kv-grid">
          <span className="muted">Slug</span>
          <code>{fullOrg.data?.slug}</code>

          <span className="muted">Your role</span>
          <span>{myRole}</span>

          <span className="muted">Members</span>
          <span>{members.length}</span>
        </div>
      </div>

      <div className="card">
        <h3>Members</h3>
        {enriched.isLoading && <p className="muted">Loading…</p>}
        {enriched.error && (
          <p className="err">Couldn't load members — {(enriched.error as Error).message}</p>
        )}
        {memberMutErr && <p className="err">{memberMutErr.message}</p>}
        {enriched.data && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <SortHeader label="Name"   columnKey="name"      sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Email"  columnKey="email"     sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Role"   columnKey="role"      sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Status" columnKey="blocked"   sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <SortHeader label="Joined" columnKey="createdAt" sortKey={memberSort.sortKey} sortDir={memberSort.sortDir} onSort={memberSort.handleSort} />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {memberSort.sorted.map((m) => {
                  const isSelf = m.userId === myUserId;
                  const canEditThis = canManage && !isSelf && m.role !== "owner";
                  return (
                    <tr key={m.id} style={m.blockedAt ? { opacity: 0.6 } : { cursor: "default" }}>
                      <td>{m.name ?? <span className="muted">—</span>}</td>
                      <td>{m.email ?? <span className="muted">—</span>}</td>
                      <td>
                        {canEditThis ? (
                          <select
                            aria-label={`Role for ${m.email ?? m.userId}`}
                            value={m.role}
                            onChange={async (e) => {
                              const r = await authClient.organization.updateMemberRole({
                                memberId: m.id,
                                role: e.target.value as "member" | "admin",
                              });
                              if (r.error) {
                                alert(`Failed: ${r.error.message ?? "unknown"}`);
                                return;
                              }
                              await refetchAll();
                            }}
                            style={{ width: "auto", padding: "0.3rem 0.5rem" }}
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                        ) : (
                          m.role
                        )}
                      </td>
                      <td>
                        {m.blockedAt
                          ? <span className="pill fail" title={m.blockedReason ?? undefined}>Blocked</span>
                          : <span className="muted">Active</span>}
                      </td>
                      <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {canEditThis && !m.blockedAt && (
                          <button
                            className="secondary"
                            onClick={() => {
                              const reason = window.prompt(`Block ${m.email ?? "this member"}? Optional reason:`);
                              if (reason !== null) {
                                blockMember.mutate({ id: m.id, reason: reason.trim() || null });
                              }
                            }}
                            disabled={blockMember.isPending}
                          >
                            Block
                          </button>
                        )}
                        {canEditThis && m.blockedAt && (
                          <button
                            className="secondary"
                            onClick={() => unblockMember.mutate(m.id)}
                            disabled={unblockMember.isPending}
                          >
                            Unblock
                          </button>
                        )}
                        {" "}
                        {canEditThis && (
                          <button
                            className="danger"
                            onClick={async () => {
                              if (!confirm(`Remove ${m.email ?? m.userId} from this organisation?`)) return;
                              const r = await authClient.organization.removeMember({
                                memberIdOrEmail: m.id,
                              });
                              if (r.error) {
                                alert(`Failed: ${r.error.message ?? "unknown"}`);
                                return;
                              }
                              await refetchAll();
                            }}
                          >
                            Remove
                          </button>
                        )}
                        {isSelf && myRole !== "owner" && (
                          <button
                            className="danger"
                            onClick={async () => {
                              if (!confirm("Leave this organisation?")) return;
                              const r = await authClient.organization.leave({
                                organizationId: orgId!,
                              });
                              if (r.error) {
                                alert(`Failed: ${r.error.message ?? "unknown"}`);
                                return;
                              }
                              window.location.assign("/");
                            }}
                          >
                            Leave
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canManage && (
        <>
          <div className="card">
            <h3>Invite a teammate</h3>
            <form
              className="formstack"
              onSubmit={async (e) => {
                e.preventDefault();
                setInviteState({ busy: true });
                const r = await authClient.organization.inviteMember({
                  email: inviteEmail,
                  role: inviteRole,
                });
                if (r.error) {
                  setInviteState({ busy: false, err: r.error.message ?? "Couldn't send invitation." });
                  return;
                }
                setInviteEmail("");
                setInviteRole("member");
                setInviteState({ busy: false, ok: `Invitation sent to ${inviteEmail}.` });
                await refetchAll();
              }}
            >
              <label>
                Email
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </label>
              <label>
                Role
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as typeof ROLES[number])}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </label>
              {inviteState.err && <div className="err">{inviteState.err}</div>}
              {inviteState.ok && <div className="muted">{inviteState.ok}</div>}
              <button type="submit" disabled={inviteState.busy || !inviteEmail}>
                {inviteState.busy ? "Sending…" : "Send invitation"}
              </button>
            </form>
          </div>

          <div className="card">
            <h3>Pending invitations</h3>
            {invitations.isLoading && <p className="muted">Loading…</p>}
            {invitations.error && (
              <p className="err">Couldn't load invitations — {(invitations.error as Error).message}</p>
            )}
            {invitations.data && invitations.data.filter((i) => i.status === "pending").length === 0 && (
              <EmptyState
                icon={<InboxIcon />}
                message="No pending invitations."
              />
            )}
            {invitations.data && invitations.data.filter((i) => i.status === "pending").length > 0 && (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Expires</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.data
                      .filter((i) => i.status === "pending")
                      .map((i) => (
                        <tr key={i.id} style={{ cursor: "default" }}>
                          <td>{i.email}</td>
                          <td>{i.role}</td>
                          <td>{new Date(i.expiresAt).toLocaleString()}</td>
                          <td>
                            <button
                              className="secondary"
                              onClick={async () => {
                                if (!confirm(`Cancel invitation to ${i.email}?`)) return;
                                const r = await authClient.organization.cancelInvitation({
                                  invitationId: i.id,
                                });
                                if (r.error) {
                                  alert(`Failed: ${r.error.message ?? "unknown"}`);
                                  return;
                                }
                                await refetchAll();
                              }}
                            >
                              Cancel
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
